import { Component, Input } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Store, select } from '@ngrx/store';

import { StateInterface, Account, Locations, Locale, Registration } from '../../../store/state.model';
import { UserMeta } from '../../../store/auth/auth.models';
import { LocaleService, LayoutService, UsersService, FilterService } from '../../../services';
import { BaseComponent } from '../../base/base.component';
import { _ } from '../../../tools';
import { Location } from '../../../store/locations/locations.models';

@Component({
	selector: 'settings',
	template: require('./settings-view.component.html')
})

/**
 * Class representing the SettingsViewComponent component.
 */
export class SettingsViewComponent extends BaseComponent {

	/**
	 * @property {Account} account - The account state.
	 */
	account: Account;

	/**
	 * @property {Locale}  locale - The locale state.
	 */
	locale: Locale;

	/**
	 * @property {Array<Location>} - All available locations
	 */
	locations: Array<Location>;

	/**
	 * @type {Registration} - The registration state.
	 */
	registration: Registration;

	/**
	 * @property {UserMeta} userMeta - The user meta data
	 */
	userMeta: UserMeta = {
		preferedLocationId: 0,
		preferedLanguage: 'nl',
	};

	locationSelected: Number = 1;

	/**
	 * Constructor.
	 * @param {LocaleService} localeService
	 * @param {LayoutService} layoutService
	 * @param {UsersService} usersService
	 * @param {FilterService} filterService
	 * @param {Router} router
	 * @param {Store} store
	 */
	constructor(
		private localeService: LocaleService,
		private layoutService: LayoutService,
		private usersService: UsersService,
		private filterService: FilterService,
		private router: Router,
		private store: Store<StateInterface>
	) {
		super();

		// Subscribes to the locale state
		this.addSubscription(store.pipe(select('locale'))
			.subscribe((locale: Locale) => {
				this.locale = _.cloneDeep(locale);
			})
		);

		// Subscribes to the locations state
		this.addSubscription(store.pipe(select('locations'))
			.subscribe((locations: Locations) => {
				this.locations = locations.items;
			})
		);

		// Subscribes to the registration state
		this.addSubscription(store.pipe(select('registration'))
			.subscribe((registration: Registration) => {
				this.registration = _.cloneDeep(registration);
			})
		);
	}

	/**
	 * Upon initializing the component.
	 * @return {void}
	 */
	ngOnInit(): void {
		window.scrollTo(0, 0);

		// Sets page-header navigation
		this.store.dispatch(
			this.layoutService.editLayout({
				leftNav: { label: 'overview-text', handler: () => this.router.navigate(['/reservation/overview']) },
				rightNav: null
			})
		);

		// Subscribes to the account state
		this.addSubscription(this.store.pipe(select('account'))
			.subscribe((account: Account) => {
				this.account = _.cloneDeep(account);
				this.userMeta = account.meta;
				this.locationSelected = this.userMeta.preferedLocationId;
			})
		);
	}

	/**
	 * Change the current language.
	 * @param {string} locale
	 * @return {void}
	 */
	onChangeLanguage(locale: string): void {
		const { meta } = this.account;
		let language = 'nl-NL';

		if (locale === 'en') {
			language = 'en-GB';
		}

		this.store.dispatch(
			this.localeService.setCurrent(locale)
		);

		this.userMeta = Object.assign({}, meta, {
			'preferedLanguage': language,
		});

		this.usersService.patchUserMeta(this.userMeta).subscribe((userDataFromApi: any) => {
			this.store.dispatch(
				this.usersService.setUserMeta(userDataFromApi),
			);
		});
	}

	/**
	 * Logout event
	 * @param event
	 */
	logout(event: any): void {
		if (event) {
			event.preventDefault();
		}
		sessionStorage.removeItem('stateSnapshot');
		location.href = '/login';
	}

	/**
	 * Sets and saves the location filter.
	 * @param location
	 */
	setAndSaveLocationFilter(locationId: any): void {
		console.log('Change setting');
		console.log(locationId);
		const { meta } = this.account;

		this.userMeta = Object.assign({}, meta, {
			'preferedLocationId': locationId,
		});

		// Saves the meta data to the DB and updates the users meta in the store.
		this.usersService.patchUserMeta(this.userMeta).subscribe((userDataFromApi: any) => {
			this.store.dispatch(
				this.usersService.setUserMeta(userDataFromApi),
			);

			this.store.dispatch(
				this.filterService.resetState(),
			);
			this.store.dispatch(
				this.filterService.editFilter({ locationId: userDataFromApi.preferedLocationId }),
			);
		});
	}
}
